import { Link } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { PROPS } from "@/lib/testIds";
import { useState } from "react";

const formatLocation = (location) => {
  if (!location) return "";
  if (typeof location === "string") return location;

  if (typeof location === "object") {
    return [location.city, location.state, location.country]
      .filter(Boolean)
      .join(", ");
  }

  return "";
};

export const PropertyCard = ({ property, variant = "default" }) => {
  const [saved, setSaved] = useState(false);

  // The homepage intentionally renders up to five editorial slots. When the
  // API returns fewer properties (or none), an empty slot must not crash the
  // entire React application.
  if (!property?.id) return null;

  const isTall = variant === "tall";
  const isWide = variant === "wide";

  const propertySlug = property.slug || property.id;
  const propertyName = property.title || property.name || "StayEasy property";
  const location = formatLocation(property.location);
  const image =
    property.cover_image?.url ||
    property.images?.[0]?.url ||
    property.images?.[0] ||
    "";
  const rating = Number(property.average_rating ?? property.rating ?? 0);
  const guests = property.max_guests ?? property.guests ?? 0;
  const bedrooms = property.bedrooms ?? property.beds ?? 0;
  const price = property.price_per_night ?? property.price ?? 0;
  const collection =
    typeof property.collection === "string"
      ? property.collection
      : property.collection?.name;

  const aspect = isTall
    ? "aspect-[3/4]"
    : isWide
    ? "aspect-[16/10]"
    : "aspect-[4/5]";

  return (
    <article
      data-testid={PROPS.card(property.id)}
      className="group relative"
    >
      <Link to={`/properties/${propertySlug}`} className="block">
        <div className={`relative overflow-hidden bg-muted ${aspect}`}>
          {image && (
            <img
              src={image}
              alt={propertyName}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
            />
          )}
          <button
            type="button"
            data-testid={PROPS.cardSave(property.id)}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setSaved((current) => !current);
            }}
            aria-label={saved ? "Unsave" : "Save"}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-background/80 opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100 focus:opacity-100"
          >
            <Heart
              size={16}
              className={
                saved ? "fill-primary stroke-primary" : "stroke-foreground"
              }
            />
          </button>
          {collection && (
            <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-background/85 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-foreground">
              {collection}
            </span>
          )}
        </div>

        <div className="mt-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            {location && (
              <p className="truncate text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {location}
              </p>
            )}
            <h3 className="mt-2 font-display text-xl font-medium tracking-tight text-foreground md:text-2xl">
              {propertyName}
            </h3>
          </div>
          <div className="flex shrink-0 items-center gap-1 text-sm text-foreground">
            <Star size={14} className="fill-foreground stroke-foreground" />
            <span>{rating.toFixed(2)}</span>
          </div>
        </div>

        <div className="mt-3 flex items-baseline justify-between">
          <p className="text-sm text-muted-foreground">
            {guests} guests · {bedrooms} bd
          </p>
          <p className="text-sm font-medium text-foreground">
            <span className="font-display text-lg">${price}</span>
            <span className="text-muted-foreground"> / night</span>
          </p>
        </div>
      </Link>
    </article>
  );
};

export default PropertyCard;
